<template>
  <div>
    <p class="text-muted">{{ $t("TRAININGS.SETTINGS_DESCRIPTION") }}</p>
    <div v-if="loading" class="text-center py-4">
      <img src="/img/loading.gif" style="height: 80px; width: 80px" />
    </div>
    <template v-else>
      <base-checkbox v-model="forceVideoCompletion" class="mb-1">
        <span class="font-weight-bold">
          {{ $t("TRAININGS.FORCE_VIDEO_COMPLETION") }}
        </span>
      </base-checkbox>
      <small class="d-block text-muted">
        {{ $t("TRAININGS.FORCE_VIDEO_COMPLETION_HELP") }}
      </small>

      <div v-if="forceVideoCompletion" class="mt-4">
        <label class="form-control-label d-block mb-0">
          {{ $t("TRAININGS.EXEMPT_USERS") }}
        </label>
        <small class="d-block text-muted mb-2">
          {{ $t("TRAININGS.EXEMPT_USERS_HELP") }}
        </small>
        <el-select
          v-model="exemptUserIds"
          multiple
          filterable
          :filter-method="(query) => (userQuery = query)"
          class="training-settings-users"
          :placeholder="$t('TRAININGS.SELECT_EMPLOYEES')"
          @visible-change="userQuery = ''"
        >
          <el-option
            v-for="user in filteredUsers"
            :key="user.id"
            :value="user.id"
            :label="`${user.firstname} ${user.lastname}`"
          >
            <span>{{ user.firstname }} {{ user.lastname }}</span>
            <small class="text-muted ml-2">{{ user.roles.join(", ") }}</small>
          </el-option>
        </el-select>
      </div>

      <div class="d-flex flex-wrap align-items-center mt-4">
        <base-button
          type="primary"
          size="sm"
          :disabled="saving || !dirty"
          @click="save"
        >
          <i class="fa fa-save mr-1"></i>
          {{ $t("COMMON.SAVE") }}
        </base-button>
        <small v-if="dirty" class="text-warning ml-2">
          {{ $t("TRAININGS.UNSAVED_CHANGES") }}
        </small>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Option, Select } from "element-ui";

function sortedIds(ids) {
  return [...ids].sort((a, b) => a - b).join(",");
}

/**
 * Réglages des formations : visionnage obligatoire des vidéos et employés
 * dispensés (déjà formés).
 */
export default {
  name: "training-settings",

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
  },

  data() {
    return {
      loading: true,
      saving: false,
      forceVideoCompletion: false,
      exemptUserIds: [],
      userQuery: "",
    };
  },

  computed: {
    ...mapGetters({ settings: "trainings/settings" }),
    dirty() {
      return (
        this.forceVideoCompletion !== this.settings.force_video_completion ||
        sortedIds(this.exemptUserIds) !==
          sortedIds(this.settings.exempt_users)
      );
    },
    // Selected employees stay listed: their tag needs the option.
    filteredUsers() {
      const query = this.$unaccent(this.userQuery.trim().toLowerCase());
      if (!query) {
        return this.settings.users;
      }
      return this.settings.users.filter(
        (user) =>
          this.exemptUserIds.includes(user.id) ||
          this.$unaccent(
            `${user.firstname} ${user.lastname} ${user.email}`.toLowerCase()
          ).includes(query)
      );
    },
  },

  async created() {
    try {
      await this.$store.dispatch("trainings/settings");
      this.reset();
    } catch (error) {
      this.$notify({
        type: "danger",
        message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
      });
    }
    this.loading = false;
  },

  methods: {
    reset() {
      this.forceVideoCompletion = this.settings.force_video_completion;
      this.exemptUserIds = [...this.settings.exempt_users];
    },

    async save() {
      this.saving = true;
      try {
        await this.$store.dispatch("trainings/updateSettings", {
          forceVideoCompletion: this.forceVideoCompletion,
          exemptUserIds: this.exemptUserIds,
        });
        this.reset();
        this.$notify({ type: "success", message: this.$t("TRAININGS.SAVED") });
        this.$emit("saved");
      } catch (error) {
        this.$showRequestError(error);
      }
      this.saving = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.training-settings-users {
  width: 100%;
  max-width: 100%;
}
</style>
